import { useState, useMemo } from "react";
import classNames from "classnames";
import { useTable, useSortBy, Column } from "react-table";
import { Item } from "@outplayed/tooltips";
import { Checkbox } from "@ugg/shared/components/common/Checkbox";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TableSortLabel } from "@ugg/shared/components/common/TableSortLabel";
import { stackDuplicateItems } from "@ugg/shared/utils/item-helpers";
import { round, numberWithCommas } from "@ugg/shared/utils/math";
import { AdvBuildInfo } from "@ugg/shared/api/data-parser/champions/adv-builds";
import { ReactComponent as ArrowRight } from "@ugg/shared/assets/svg/arrow-right.svg";

interface CoreItemsTableProps {
  className?: string;
  coreItems: AdvBuildInfo["core_items"];
  coreItemsWithBoots: AdvBuildInfo["core_items_with_boots"];
  spriteSize: number;
  patch?: string;
  language?: string;
  ssr?: boolean;
  webp?: boolean;
}

export function CoreItemsTable(props: CoreItemsTableProps) {
  const { className, coreItems, coreItemsWithBoots, spriteSize, patch, language, ssr, webp } = props;

  const [withBoots, setWithBoots] = useState(false);
  const data = withBoots ? coreItemsWithBoots : coreItems;

  const totalMatches = data.reduce((acc, curr) => {
    return (acc += curr.matches);
  }, 0);

  const columns = useMemo<Column<AdvBuildInfo["core_items" | "core_items_with_boots"][number]>[]>(
    () => [
      {
        accessor: "ids",
        className: "flex-1",
        Cell: ({ cell: { value } }) => {
          const stackedItems = stackDuplicateItems(value);
          return (
            <div className="flex items-center gap-[6px]">
              {stackedItems.map((item, index) => (
                <div key={item.id} className="flex items-center gap-[6px]">
                  <div className="relative">
                    <Item
                      className="rounded-[3px]"
                      itemId={item.id}
                      spriteSize={spriteSize}
                      isSprite
                      patch={patch}
                      language={language}
                      ssr={ssr}
                      webp={webp}
                    />
                    {item.count > 1 && (
                      <div
                        className={`
                        absolute bottom-[-4px] right-[-4px] z-10 flex items-center justify-center 
                        w-[14px] h-[14px] text-white text-[11px] font-bold rounded-[50%] border-[1px] 
                        border-purple-400 bg-accent-purple-700 pointer-events-none
                      `}
                      >
                        {item.count}
                      </div>
                    )}
                  </div>
                  {index !== stackedItems.length - 1 && <ArrowRight className="w-[12px] h-[12px]" />}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        accessor: "win_rate",
        className: "w-[60px] text-center",
        headerClassName: "w-[60px] whitespace-nowrap",
        Header: <div>Win Rate</div>,
        Cell: (props) => {
          return <div className="text-[14px] font-medium text-lavender-50">{`${round(props.value, 2)}% `}</div>;
        },
        sortDescFirst: true,
      },
      {
        accessor: "matches",
        className: "w-[65px] text-center",
        headerClassName: "w-[65px] whitespace-nowrap",
        Header: <div>Pick Rate</div>,
        Cell: ({ cell: { value } }) => {
          return (
            <div>
              <div className="text-white text-[14px] font-bold">{`${round((value / totalMatches) * 100, 2)}% `}</div>
              <div className="text-[12px] font-medium text-lavender-50">{numberWithCommas(value)}</div>
            </div>
          );
        },
        sortDescFirst: true,
      },
    ],
    [],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "matches", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  return (
    <div className={classNames("rounded-[3px] overflow-hidden", className)}>
      <div className="flex items-end justify-between p-[18px] bg-purple-200">
        <div className="flex flex-wrap whitespace-nowrap gap-[16px]">
          <HeaderBullet>
            <div>Core Items</div>
          </HeaderBullet>
          <div className="flex items-center">
            <Checkbox checkboxId="with-boots" isChecked={withBoots} onChange={() => setWithBoots((prev) => !prev)} />
            <label
              className="ml-[8px] text-[14px] font-normal text-accent-gray-100 leading-[16px] cursor-pointer"
              htmlFor="with-boots"
            >
              With Boots
            </label>
          </div>
        </div>
        <div {...getTableProps({ className: "" })}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "flex items-center gap-[24px]" })}>
              {headerGroup.headers.map((column) => {
                return (
                  <div className="contents" {...column.getHeaderProps(column.getSortByToggleProps({}))}>
                    <TableSortLabel
                      className={classNames(
                        "flex items-center justify-center text-[14px]",
                        {
                          "text-white font-semibold": column.isSorted,
                          "text-lavender-50 font-medium": !column.isSorted,
                        },
                        column.headerClassName,
                      )}
                      active={column.isSorted}
                      direction={column.isSortedDesc ? "desc" : "asc"}
                      labelOffset={5}
                    >
                      {column.render("Header")}
                    </TableSortLabel>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div {...getTableBodyProps({ className: "" })}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps({
                className: "flex items-center gap-[24px] px-[18px] py-[12px] border-t-[1px] border-purple-500 bg-purple-200",
              })}
            >
              {row.cells.map((cell) => {
                const tdClassNames = classNames(cell.column.className);
                return <div {...cell.getCellProps({ className: tdClassNames })}>{cell.render("Cell", { index: i })}</div>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
